import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Github, Twitter, Instagram, Mail, Phone, MapPin } from "lucide-react";
import LanguageToggle from "./LanguageToggle";
import { openCookieSettings } from "../../lib/consent";
import { Wordmark } from "../ui/BrandMark";
import settings from "../../data/settings.json";
import { SOLUTION_KEYS, solutionPath } from "../../lib/solutions";
import useLang from "../../hooks/useLang";

const Footer = () => {
  const { t, isRTL, dir } = useLang();
  const year = new Date().getFullYear();

  const companyLinks = [
    { to: "/about", label: t("footer.links.about") },
    { to: "/services", label: t("nav.services") },
    { to: "/projects", label: t("nav.projects") },
    { to: "/careers", label: t("footer.links.careers") },
    { to: "/contact", label: t("footer.links.contact") },
  ];

  const serviceLinks = SOLUTION_KEYS.map((key) => ({
    to: solutionPath(key),
    label: t(`whatWeDo.categories.${key}.title`),
  }));

  const socialLinks = [
    { href: settings.social.linkedin, icon: Linkedin, label: "LinkedIn" },
    { href: settings.social.github, icon: Github, label: "GitHub" },
    { href: settings.social.twitter, icon: Twitter, label: "Twitter" },
    { href: settings.social.instagram, icon: Instagram, label: "Instagram" },
  ];

  const heading = "font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-white/40 mb-5";
  const link = "text-white/70 hover:text-white text-sm transition-colors";

  return (
    <footer className="bg-ink text-white border-t border-white/10" dir={dir}>
      <div className="container-site py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" className="inline-flex mb-5" aria-label="Applumo">
              <Wordmark variant="onDark" />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-xs">{t("footer.tagline")}</p>
            <div className="flex items-center gap-2">
              {socialLinks.map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg border border-white/10 hover:border-rose hover:text-rose-light flex items-center justify-center text-white/50 transition-colors"
                  aria-label={label}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className={heading}>{t("footer.company")}</h3>
            <ul className="space-y-3">
              {companyLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className={link}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={heading}>{t("footer.services")}</h3>
            <ul className="space-y-3">
              {serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className={link}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className={heading}>{t("footer.connect")}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm">
                <Mail size={14} className="text-rose-light flex-shrink-0" />
                <a href={`mailto:${settings.company.email}`} className={link}>{settings.company.email}</a>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/70">
                <Phone size={14} className="text-rose-light flex-shrink-0" />
                <span dir="ltr">{settings.company.phone}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-white/70">
                <MapPin size={14} className="text-rose-light flex-shrink-0" />
                <span>{isRTL ? settings.company.addressAr : settings.company.address}</span>
              </li>
            </ul>
            <div className="mt-6">
              <LanguageToggle scrolled={false} />
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {year} Applumo. {t("footer.rights")}
          </p>
          <div className="flex items-center gap-5">
            <button type="button" onClick={openCookieSettings} className="text-white/40 hover:text-white/70 text-sm transition-colors">
              {t("cookies.settings")}
            </button>
            <Link to="/admin" className="text-white/30 hover:text-white/60 text-sm transition-colors">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
